import axios from "axios";
import { buildP2PStats } from "./stats";
import { ALLOWED_FIATS, DEFAULT_P2P_LIMIT } from "./types";
import { formatRequestError } from "./errors";

const KUCOIN_ENDPOINT = "/api/kucoin-p2p/_api/fiat-p2p/order/ad/list";

/**
 * @param {Object} options
 * @param {"BRL"|"BOB"} options.fiat
 * @param {"buy"|"sell"|"BUY"|"SELL"} options.side
 * @param {number} [options.page]
 * @param {number} [options.pageSize]
 * @returns {Promise<import("./types").P2PQuote>}
 */
export async function fetchKucoinP2P({
  fiat,
  side,
  page = 1,
  pageSize = DEFAULT_P2P_LIMIT,
}) {
  const normalizedFiat = String(fiat || "").toUpperCase();
  if (!ALLOWED_FIATS.includes(normalizedFiat)) {
    throw new Error("Fiat inválido para KuCoin P2P");
  }
  const normalizedSide = String(side || "").toUpperCase();
  if (!["BUY", "SELL"].includes(normalizedSide)) {
    throw new Error("Side inválido para KuCoin P2P");
  }
  const payload = {
    currency: normalizedFiat,
    cryptoCurrency: "USDT",
    side: normalizedSide,
    page,
    pageSize,
  };
  const ensureJsonResponse = (exchange, fiat, candidate) => {
    const hasJson =
      candidate &&
      typeof candidate.data === "object" &&
      candidate.data !== null;
    if (hasJson) return;
    if (import.meta.env.MODE === "development") {
      const preview = typeof candidate?.data === "string"
        ? candidate.data.slice(0, 200)
        : "";
      console.warn(
        `[P2P][${exchange}][${fiat}] resposta não-JSON/WAF`,
        { status: candidate?.status, preview }
      );
    }
    const error = new Error("Resposta não-JSON/WAF da KuCoin");
    error.response = candidate;
    throw error;
  };
  let response;
  try {
    response = await axios.post(KUCOIN_ENDPOINT, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    ensureJsonResponse("kucoin", normalizedFiat, response);
  } catch (error) {
    throw new Error(
      formatRequestError({
        exchange: "kucoin",
        fiat: normalizedFiat,
        side: normalizedSide,
        fallback: "Indisponível",
        error,
      })
    );
  }
  const items = Array.isArray(response.data?.data?.items)
    ? response.data.data.items
    : [];
  const prices = items
    .map((item) => Number(item?.price))
    .filter((price) => Number.isFinite(price));
  if (!prices.length) {
    throw new Error("Não foi possível obter anúncios válidos da KuCoin");
  }
  return buildP2PStats({
    exchange: "kucoin",
    fiat: normalizedFiat,
    side: normalizedSide,
    prices,
    experimental: true,
    raw: import.meta.env.MODE === "development" ? items : undefined,
  });
}
